// External Imports
import {
  Button,
  Card,
  Display,
  Heading,
  IconPlay,
  Inline,
  Layout,
  ShortText,
  Stack,
  Title,
  pxToRem,
  styled,
} from '@lessonnine/design-system.lib';
import { h } from 'preact';
import { useState } from 'preact/hooks';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

// Internal Imports
import { EmbeddedVideo } from '../EmbeddedVideo/EmbeddedVideo';
import { LayoutWithSiteMaxWidth } from '../LayoutWithSiteMaxWidth/LayoutWithSiteMaxWidth';
import { useStore } from '../../state/context';
import computerIcon from '../../assets/images/icons/computer-icon.svg';
import heroDesktop from '../../assets/images/hero-desktop.webp';
import heroMobile from '../../assets/images/hero-mobile.webp';
import starFillIcon from '../../assets/images/icons/star-fill-icon.svg';
import starHalfFillIcon from '../../assets/images/icons/star-half-fill-icon.svg';

// Local Variables
const desktopBreakpoint = parseInt(tokens.viewports.breakpoints.medium, 10);
const HeroSectionWrapper = styled(Stack)`
  background-color: ${({ theme }) => theme.color.brand.default};
  text-align: center;
`;

const LeftColumn = styled(Stack)`
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    text-align: left;
  }
`;

const RightColumn = styled(Stack)`
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    min-width: ${pxToRem(607)};
  }
`;

const DesktopTitleContainer = styled(Stack)`
  display: none;
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    display: block;
  }
`;

const MobileTitleContainer = styled(Stack)`
  display: block;
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    display: none;
  }
`;

const StartSpeaking = styled(Heading)`
  max-width: ${pxToRem(330)};
`;

const LanguageCardContainer = styled.ul`
  display: grid;
  gap: ${pxToRem(parseInt(tokens.space.x2, 10))};
  grid-template-columns: 1fr 1fr;
  padding: 0;

  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    gap: ${pxToRem(parseInt(tokens.space.x4, 10))};
  }
`;

const LanguageCardLink = styled.a`
  text-decoration: none;
  width: 100%;
`;

const LanguageCard = styled(Card)`
  user-select: none;
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    min-width: ${pxToRem(180)};
  }
`;

const FullAccessIcon = styled.img`
  height: ${pxToRem(parseInt(tokens.space.x10, 10))};
  width: ${pxToRem(parseInt(tokens.space.x10, 10))};
`;

const HeroImages = styled.div`
  aspect-ratio: 750 / 259;
  background: center / contain no-repeat url('${heroMobile}');
  width: 100%;

  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    aspect-ratio: 1382 / 1041;
    background-image: url('${heroDesktop}');
  }
`;

const StarIcon = styled.img`
  height: ${pxToRem(20)};
  width: ${pxToRem(20)};

  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    height: ${pxToRem(32)};
    width: ${pxToRem(32)};
  }
`;

// Component Definition
function HeroSection() {
  const { getDropdownLink, learnLanguages, t } = useStore();
  const [showEmbeddedVideo, setEmbedState] = useState(false);

  const openIframe = (event) => {
    if (event.type === 'click' || (event.type === 'keydown' && event.key === 'Enter')) {
      setEmbedState(true);
    }
  };

  return (
    <HeroSectionWrapper
      align="center"
      alignItems="center"
      gap={{ medium: tokens.space.x24, xxxsmall: tokens.space.x8 }}
      padding={{ medium: [pxToRem(80), pxToRem(155)], xxxsmall: [pxToRem(48), 0] }}
    >
      <LayoutWithSiteMaxWidth
        align={{ medium: 'spaceBetween', xxxsmall: 'center' }}
        alignItems={{ medium: 'leading', xxxsmall: 'center' }}
        direction={{ medium: 'inline', xxxsmall: 'stack' }}
        gap={tokens.space.x8}
      >
        <LeftColumn
          align="center"
          alignItems={{ medium: 'leading', xxxsmall: 'center' }}
          gap={tokens.space.x8}
        >
          <DesktopTitleContainer>
            <Display color="inverse" italic size="small">
              {t.hero.headline1.part1}
            </Display>
            <Display color="inverse" size="small">
              {t.hero.headline1.part2}
            </Display>
            <Display color={tokens.color.theme.light.illustration.tangerine.light} size="small">
              {t.hero.headline2}
            </Display>
          </DesktopTitleContainer>
          <MobileTitleContainer>
            <Title color="inverse" italic>
              {t.hero.headline1.part1}
            </Title>
            <Title color="inverse">{t.hero.headline1.part2}</Title>
            <Title color={tokens.color.theme.light.illustration.tangerine.light}>
              {t.hero.headline2}
            </Title>
          </MobileTitleContainer>
          <StartSpeaking color="inverse" size={{ medium: 'default', small: 'small' }}>
            {t.hero.subheadline}
          </StartSpeaking>
          <LanguageCardContainer>
            {learnLanguages.map(({ code, Icon, name }) => (
              <Inline as="li" key={name}>
                <LanguageCardLink href={getDropdownLink(code)}>
                  <LanguageCard color="lightened">
                    <Layout
                      align={{ medium: 'leading', small: 'center' }}
                      alignItems="center"
                      direction={{ medium: 'row', small: 'column' }}
                      gap={pxToRem(parseInt(tokens.space.x2, 10))}
                    >
                      <Icon />
                      <ShortText size="small" weight="strong">
                        {name}
                      </ShortText>
                    </Layout>
                  </LanguageCard>
                </LanguageCardLink>
              </Inline>
            ))}
          </LanguageCardContainer>
          <Inline alignItems="center" gap={tokens.space.x2}>
            <FullAccessIcon alt="" src={computerIcon} />
            <ShortText color="inverse" size={{ medium: 'large', small: 'default' }} weight="strong">
              {t.hero.perk}
            </ShortText>
          </Inline>
        </LeftColumn>
        <RightColumn align="center" alignItems="center" gap={tokens.space.x8}>
          <HeroImages />
          <Button color="secondary-default" onClick={openIframe} onKeyDown={openIframe}>
            <Inline alignItems="center" gap={tokens.space.x2}>
              <IconPlay />
              <ShortText size={{ medium: 'default', small: 'small' }} weight="strong">
                {t.hero.video_cta}
              </ShortText>
            </Inline>
          </Button>
          {showEmbeddedVideo && (
            <EmbeddedVideo
              buttonLabel={t.video.close_button}
              link={t.video.link}
              setEmbedState={setEmbedState}
              title={t.video.label}
            />
          )}
        </RightColumn>
      </LayoutWithSiteMaxWidth>
      <Stack alignItems="center" gap={tokens.space.x4}>
        <Inline as="ul" padding={0}>
          <Inline as="li">
            <StarIcon alt="" src={starFillIcon} />
          </Inline>
          <Inline as="li">
            <StarIcon alt="" src={starFillIcon} />
          </Inline>
          <Inline as="li">
            <StarIcon alt="" src={starFillIcon} />
          </Inline>
          <Inline as="li">
            <StarIcon alt="" src={starFillIcon} />
          </Inline>
          <Inline as="li">
            <StarIcon alt="" src={starHalfFillIcon} />
          </Inline>
        </Inline>
        <ShortText color="inverse" size={{ medium: 'large', small: 'default' }} weight="strong">
          {t.hero.rating}
        </ShortText>
      </Stack>
    </HeroSectionWrapper>
  );
}

// Module Exports
export { HeroSection };
